import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, User, LayoutDashboard, LogOut } from "lucide-react";
import { ASSETS, NAV_LINKS } from "@/data/content";
import { scrollToId } from "@/components/Motion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
 
const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
 
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
 
  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 400);
    } else {
      scrollToId(id);
    }
  };
 
  const solid = scrolled || location.pathname !== "/";
 
  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${solid
          ? "bg-[#07070c]/80 backdrop-blur-xl border-b border-[#3d414f]"
          : "bg-transparent border-b border-transparent"
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => (location.pathname === "/" ? go("hero-brand") : navigate("/"))} className="flex items-center gap-3 group">
          <img src={ASSETS.rhino} alt="Hacktonomy" className="h-10 w-10 object-contain transition-transform duration-500 group-hover:-translate-y-0.5" />
          <span className="font-display font-700 text-lg tracking-tight text-[#97b8ff]">hackTonomy</span>
        </button>
 
        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button key={l.id} data-testid={`nav-link-${l.id}`} onClick={() => go(l.id)}
              className="font-mono2 text-xs uppercase tracking-[0.18em] text-[#97b8ff] hover:text-[#16fcfb] transition-colors duration-300">
              {l.label}
            </button>
          ))}
 
          {user ? (
            <div className="flex items-center gap-3">
              <button data-testid="nav-dashboard-btn" onClick={() => navigate("/user-panel")}
                className="flex items-center gap-2 rounded-full border border-[#3d414f] bg-[#404452]/40 pl-1.5 pr-4 py-1.5 hover:border-[#16fcfb] transition-colors">
                <span className="grid h-7 w-7 place-items-center rounded-full bg-[#16fcfb] text-[#07070c]"><User size={15} /></span>
                <span className="font-mono2 text-[11px] uppercase tracking-widest text-white max-w-[120px] truncate">
                  {user.name?.split(" ")[0] || "Account"}
                </span>
              </button>
              <button data-testid="nav-logout-btn" onClick={() => { logout(); navigate("/"); }}
                title="Sign out" className="text-white/60 hover:text-[#16fcfb] transition-colors">
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <>
 
              <Button data-testid="nav-register-btn" onClick={() => navigate("/sign-up")}
                className="rounded-full bg-[#16fcfb] text-[#07070c] hover:bg-[#97b8ff] font-mono2 text-xs uppercase tracking-widest px-6">
                <User size={16} /> Sign Up
              </Button>
            </>
          )}
        </nav>
 
        <button data-testid="nav-mobile-toggle" className="md:hidden text-white" onClick={() => setOpen((o) => !o)} aria-label="Toggle menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
 
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#07070c]/95 backdrop-blur-xl border-b border-[#3d414f]">
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <button key={l.id} data-testid={`nav-mobile-link-${l.id}`} onClick={() => go(l.id)}
                  className="text-left font-mono2 text-sm uppercase tracking-[0.18em] text-[#97b8ff]">
                  {l.label}
                </button>
              ))}
              {user ? (
                <>
                  <button data-testid="nav-mobile-dashboard-btn" onClick={() => { setOpen(false); navigate("/dashboard"); }}
                    className="flex items-center gap-2 font-mono2 text-sm uppercase tracking-widest text-white">
                    <LayoutDashboard size={16} /> Dashboard
                  </button>
                  <button data-testid="nav-mobile-logout-btn" onClick={() => { setOpen(false); logout(); navigate("/"); }}
                    className="flex items-center gap-2 font-mono2 text-sm uppercase tracking-widest text-white/70">
                    <LogOut size={16} /> Sign Out
                  </button>
                </>
              ) : (
                <>
 
                  <Button data-testid="nav-mobile-register-btn" onClick={() => { setOpen(false); navigate("/sign-up"); }}
                    className="rounded-full bg-[#16fcfb] text-[#07070c] font-mono2 text-xs uppercase tracking-widest">
                    <User size={16} /> Sign Up
                  </Button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
 
export default Nav;
 
 